import has from 'lodash/has';
import React, { useCallback, useEffect, useMemo } from 'react';

import { DefaultItemProps, SelectProps } from './Select';
import { SelectField } from './SelectField';

export interface SingleSelectProps<Item = DefaultItemProps, Values = any>
  extends Omit<SelectProps<Item, Values>, 'isMultiselect'> {
  currentValue: string | string[] | null;
  valueIsArray: boolean;
  noAvailableOptions: boolean;
  getItems: (newOptions?: Item[]) => Item[];
  errorMessage?: string;
  handleEmptyValue?: { showEmptyValue: boolean; warningText: string };
}

export const SingleSelect = <Item extends DefaultItemProps, Values>({
  name,
  options,
  onChange,
  onSearch,
  onClearSearch,
  selectFirstByDefault,
  allowCustomValues,
  currentValue,
  valueIsArray,
  noAvailableOptions,
  getItems,
  errorMessage,
  ...props
}: SingleSelectProps<Item, Values>) => {
  const { values, setFieldValue } = props;

  const items = useMemo(() => {
    let newOptions;

    const valueExists = currentValue && options?.find((option) => option.value === currentValue);

    if (!valueExists && currentValue && allowCustomValues) {
      newOptions = [
        {
          label: currentValue.toString(),
          value: currentValue.toString(),
        },
      ] as Item[];
    }

    return getItems(newOptions);
  }, [currentValue, options]);

  const selected = useMemo(() => {
    return items?.find((option) => !option.disabled && option.value === currentValue);
  }, [items, currentValue]);

  const onSelect = useCallback(
    (item: Item, validate = true) => {
      onChange?.(item);
      setFieldValue(name, item.value, validate);
    },
    [onChange, name],
  );

  useEffect(() => {
    if (!noAvailableOptions && !has(values, name) && options?.length) {
      if (selectFirstByDefault && items[0]) {
        onSelect?.(items[0] as Item); // selecting by default should go the normal step of setting other form values besides this one
      } else {
        setFieldValue(name, '', false);
      }
    } else if (has(values, name)) {
      if (valueIsArray && currentValue && currentValue.length > 0) {
        // if we are switching from multiselect to normal dropdown,
        // convert array to string by keeping the first value only
        setFieldValue(name, currentValue[0].toString(), false);
      }
    }
  }, []);

  const onFilter = useCallback(
    (e) => {
      onSearch?.(e.target.value);
    },
    [onSearch],
  );

  return (
    <SelectField<Item>
      {...props}
      name={name}
      noAvailableOptions={noAvailableOptions}
      items={items}
      selectedItem={selected}
      onSelect={onSelect}
      onFilter={onSearch ? onFilter : undefined}
      onClearSearch={onClearSearch}
      errorMessage={errorMessage}
    />
  );
};
